import { useState } from "react";
import { intlLocaleOf, type Locale } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { Button, DismissibleBanner, useToast } from "@wato/ui";
import { useApi, useErrorMessage } from "../../../api/context";
import { useWorkspace } from "../../workspace/WorkspaceContext";

const DAY_MS = 24 * 60 * 60 * 1000;

export function daysLeft(scheduledAt: string, now: number = Date.now()): number {
  return Math.max(0, Math.ceil((Date.parse(scheduledAt) - now) / DAY_MS));
}

export function formatDeletionDate(scheduledAt: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocaleOf(locale), { dateStyle: "long" }).format(
    new Date(scheduledAt),
  );
}

/** Owner-only "Cancel deletion" (restores full access immediately, WP-152). */
export function CancelDeletionButton({ size = "md" }: { size?: "sm" | "md" }) {
  const { t } = useI18n();
  const api = useApi();
  const toast = useToast();
  const errorMessage = useErrorMessage();
  const { workspace, reload } = useWorkspace();
  const [busy, setBusy] = useState(false);

  if (!workspace.isOwner) {
    return <p className="text-sm text-fg-muted">{t("workspace.lifecycle.ownerOnly")}</p>;
  }

  async function cancel() {
    setBusy(true);
    try {
      await api.cancelDeletion(workspace.id);
      toast.show({ tone: "success", title: t("workspace.lifecycle.cancelled") });
      await reload();
    } catch (caught) {
      toast.show({ tone: "error", title: errorMessage(caught) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button variant="secondary" size={size} loading={busy} onClick={() => void cancel()}>
      {t("workspace.lifecycle.cancelDeletion")}
    </Button>
  );
}

/**
 * "Pending deletion, N days left, Cancel" banner shown on every page of a workspace in its
 * 30-day grace window (WP-152). The workspace is read-only until then.
 */
export function PendingDeletionBanner() {
  const { t } = useI18n();
  const { workspace } = useWorkspace();
  if (!workspace.deletion) return null;
  return (
    <DismissibleBanner
      id="pending-deletion"
      priority={20}
      severity="warning"
      dismissible={false}
      testId="pending-deletion-banner"
      action={<CancelDeletionButton size="sm" />}
    >
      {t("workspace.lifecycle.pendingBanner", {
        days: daysLeft(workspace.deletion.scheduledAt),
      })}
    </DismissibleBanner>
  );
}
